import React, { useCallback } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Tooltip } from 'ui-components/components/tooltip';
import { testId } from 'ui-components/services/testid-service';

import style from './shared-classes.module.less';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { NewReturnFulfillmentId } from '../../orders/order-details/hooks/use-pending-return';
import { useReturnNavigation } from '../../orders/order-details/hooks/use-return-navigation';
import Global from '../../store/global-selectors';
import { orderDetailsModalOpenedFromShipmentForReturn } from '../../store/orders/order-details/orders.details.actions';
import { useCanCreateEnhancedReturn } from '../../store/returns/returns.helpers';
import { EventSourceStrings, Workflows } from '../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../tracking/use-workflow-tracking';

const messages = defineMessages({
  buttonLabel: {
    description: 'grid header modal create return button label',
    defaultMessage: 'Create Return',
    id: 'juBYF/',
  },
  disabledButtonLabelTooltip: {
    description: 'grid header create return tooltip message',
    defaultMessage: "A return label cannot be made from the shipment record for an outbound label that doesn't have an order.",
    id: '3GUhs2',
  },
});

export const CreateReturnButton = () => {
  const dispatch = useDispatch();
  const selectedFulfillmentIds = useSelector(Global.selectSelectedShipmentsSelectedShipmentIds) || emptyArray;
  const isOneShipmentSelected = selectedFulfillmentIds.length === 1;
  const selectedFulfillmentId = selectedFulfillmentIds[0];
  const { fulfillmentPlanId } =
    useSelector((state) =>
      Global.selectEntitiesFulfillmentById(state, {
        fulfillmentId: selectedFulfillmentId,
      }),
    ) || emptyObject;
  const { salesOrdersFulfilledIds = emptyArray } =
    useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId })) || emptyObject;
  const salesOrderId = salesOrdersFulfilledIds[0];
  const disabled = !isOneShipmentSelected || !salesOrderId;
  const hasToolTip = !salesOrderId && isOneShipmentSelected;
  const trackCreateReturn = useTrackWorkflow(Workflows.CREATE_RETURN_FLOW);
  const shouldUseEnhancedReturn = useCanCreateEnhancedReturn(salesOrderId);

  const { navigateToReturn } = useReturnNavigation();

  const createReturn = useCallback(() => {
    trackCreateReturn(WorkflowSteps.START, {
      enhanced: shouldUseEnhancedReturn,
      fulfillmentPlanId,
      salesOrderId,
      source: EventSourceStrings.SHIPMENTS_GRID_HEADER,
    });
    dispatch(
      orderDetailsModalOpenedFromShipmentForReturn({
        isReturnsFromShipment: true,
        fulfillmentPlanId,
      }),
    );
    navigateToReturn(salesOrderId, NewReturnFulfillmentId, shouldUseEnhancedReturn);
  }, [dispatch, fulfillmentPlanId, navigateToReturn, salesOrderId, shouldUseEnhancedReturn, trackCreateReturn]);

  return (
    <Tooltip position='bottom' shouldHideTooltip={!hasToolTip} text={<FormattedMessage {...messages.disabledButtonLabelTooltip} />}>
      <div className={style['create-return-button-wrapper']}>
        <Button
          {...testId('create-returns-button')}
          className={style['action-button']}
          disabled={disabled}
          onClick={createReturn}
          theme='tertiary'
        >
          <FormattedMessage {...messages.buttonLabel} />
        </Button>
      </div>
    </Tooltip>
  );
};
